// Bulk edit — "Edit visit details" across several visits. RON-5466 Phase 3.
//
// SCOPE: the shared logistics of a visit — when, where, and who is hosting. Deliberately NOT:
//
//   name / email / company  per-person, so editing them in bulk would overwrite three people
//                           with one person's details. Edit those one visit at a time.
//   visitor type (VIP)      no plausible reason to promote or demote a batch at once; left out
//                           rather than shipped as a control nobody asked for.
//
// ONLY FIELDS THE HOST ACTUALLY CHANGED ARE SENT. That is the whole design problem here: a bulk
// form pre-filled with one row's values would, on save, quietly stamp those values over every
// other row. So each field starts at the selection's common value (blank when they differ) and is
// only included in the payload once touched — see `touched`.

import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import CustomDatePicker from "../../Common/CustomDatePicker.jsx";
import CustomSearchableDropdown from "../../Common/CustomSearchableDropdown.jsx";
import VisitorFormRow, { fieldClassName } from "./VisitorFormRow.jsx";
import { pluralText } from "../visitorConstants.js";
import { fromISODate, isSameOrBefore, toISODate } from "../visitorDateUtils.js";

// One value if every selected visit agrees, otherwise null — "leave this alone".
const commonValue = (visits, key) => {
  if (visits.length === 0) return null;

  const first = visits[0][key] ?? null;

  return visits.every((visit) => (visit[key] ?? null) === first) ? first : null;
};

const FIELDS = ["startDate", "endDate", "arrivalTime", "site", "host"];

const VisitorBulkEditScreen = ({
  visits,
  sites,
  isSubmitting,
  onSubmit,
  onBack,
}) => {
  const { t } = useTranslation("common");

  // Computed once from the selection. Recomputing on every render would fight the user's edits.
  const initial = useMemo(
    () =>
      FIELDS.reduce(
        (acc, key) => ({ ...acc, [key]: commonValue(visits, key) }),
        { building: commonValue(visits, "building") }
      ),
    [visits]
  );

  const [form, setForm] = useState(initial);
  const [touched, setTouched] = useState(() => new Set());
  const [errors, setErrors] = useState({});
  // §7 — "Send update email to all visitors". ON by default, as the server defaults it and the
  // prototype draws it.
  const [sendEmail, setSendEmail] = useState(true);

  const selectedSite = sites.find((site) => site.name === form.site) || null;
  const buildings = selectedSite?.buildings || [];

  const setField = (key, value) => {
    setForm((current) => {
      const next = { ...current, [key]: value };

      // Same rule the invite wizard enforces: a range cannot end before it starts.
      if (key === "startDate" && next.endDate && !isSameOrBefore(value, next.endDate)) {
        next.endDate = value;
      }

      // A building must belong to its site (db-schema §0.3), so changing site clears it.
      if (key === "site") next.building = null;

      return next;
    });

    setTouched((current) => {
      const next = new Set(current);
      next.add(key);
      // The two coupled fields above are changed for the user, so they must be sent too.
      if (key === "startDate") next.add("endDate");
      if (key === "site") next.add("building");
      return next;
    });

    setErrors({});
  };

  // Exactly what will be written, in the order the summary lists it.
  const changes = useMemo(
    () =>
      ["startDate", "endDate", "arrivalTime", "site", "building", "host"]
        .filter((key) => touched.has(key))
        .map((key) => ({ key, value: form[key] })),
    [touched, form]
  );

  const submit = () => {
    const found = {};

    // A touched field that has been emptied would blank it on every selected visit — almost
    // certainly a mistake rather than an intent, so block it instead of guessing.
    if (touched.has("host") && !String(form.host || "").trim()) {
      found.host = "react_visitors_error_host";
    }
    if (Object.keys(found).length) {
      setErrors(found);
      return;
    }

    onSubmit(
      changes.reduce((payload, { key, value }) => ({ ...payload, [key]: value }), {}),
      sendEmail
    );
  };

  const labelFor = (key) =>
    ({
      startDate: t("react_visitors_field_start_date"),
      endDate: t("react_visitors_field_end_date"),
      arrivalTime: t("react_visitors_field_arrival"),
      site: t("react_visitors_field_site"),
      building: t("react_visitors_field_building"),
      host: t("react_visitors_field_host"),
    }[key] || key);

  // Shown under a field whose selected visits do not currently agree, so a blank control reads as
  // "these differ" rather than "this visit has no value".
  const mixedHint = (key) =>
    initial[key] === null && !touched.has(key)
      ? t("react_visitors_bulk_mixed")
      : undefined;

  return (
    <div className="visitor-bulk-edit">
      <div className="visitor-card">
        <p className="visitor-card__title">
          {pluralText(t, "react_visitors_bulk_editing", visits.length)}
        </p>
        <ul className="visitor-review__people">
          {visits.map((visit) => (
            <li className="visitor-review__person" key={visit.id}>
              <span className="visitor-review__person-name">{visit.name}</span>
              <span className="visitor-review__person-email">{visit.email}</span>
            </li>
          ))}
        </ul>
        <p className="visitor-card__hint">{t("react_visitors_bulk_hint")}</p>
      </div>

      <div className="visitor-card">
        <p className="visitor-card__title">{t("react_visitors_visit_title")}</p>

        <div className="visitor-field-grid">
          <VisitorFormRow
            id="bulk-start-date"
            label={labelFor("startDate")}
            hint={mixedHint("startDate")}
          >
            <CustomDatePicker
              id="bulk-start-date"
              selected={fromISODate(form.startDate)}
              minDate={new Date()}
              onChange={(date) => setField("startDate", toISODate(date))}
            />
          </VisitorFormRow>

          <VisitorFormRow
            id="bulk-end-date"
            label={labelFor("endDate")}
            hint={mixedHint("endDate")}
          >
            <CustomDatePicker
              id="bulk-end-date"
              selected={fromISODate(form.endDate)}
              minDate={fromISODate(form.startDate) || new Date()}
              onChange={(date) => setField("endDate", toISODate(date))}
            />
          </VisitorFormRow>
        </div>

        <div className="visitor-field-grid">
          <VisitorFormRow
            id="bulk-arrival-time"
            label={labelFor("arrivalTime")}
            hint={mixedHint("arrivalTime")}
          >
            <input
              id="bulk-arrival-time"
              type="time"
              className={fieldClassName(form.arrivalTime, null, false)}
              value={form.arrivalTime || ""}
              onChange={(event) => setField("arrivalTime", event.target.value)}
            />
          </VisitorFormRow>

        </div>

        <VisitorFormRow label={labelFor("site")} hint={mixedHint("site")}>
          <CustomSearchableDropdown
            items={sites}
            value={form.site}
            labelKey="name"
            valueKey="name"
            searchable={false}
            placeholder={t("react_visitors_bulk_unchanged")}
            onChange={(name) => setField("site", name)}
          />
        </VisitorFormRow>

        {/* Only once a site is settled for the whole selection — a building cannot be chosen
            while the selected visits are still spread across different sites. */}
        {buildings.length > 1 && (
          <VisitorFormRow label={labelFor("building")}>
            <CustomSearchableDropdown
              items={buildings}
              value={form.building}
              labelKey="name"
              valueKey="name"
              searchable={false}
              // Same placeholder as every other field here: an untouched control means "leave
              // these visits alone", not "this visit has no building".
              placeholder={t("react_visitors_bulk_unchanged")}
              onChange={(name) => setField("building", name)}
            />
          </VisitorFormRow>
        )}

        <VisitorFormRow
          id="bulk-host"
          label={labelFor("host")}
          hint={mixedHint("host")}
          error={errors.host ? t(errors.host) : null}
        >
          <input
            id="bulk-host"
            type="text"
            placeholder={t("react_visitors_bulk_unchanged")}
            className={fieldClassName(form.host, errors.host)}
            value={form.host || ""}
            onChange={(event) => setField("host", event.target.value)}
          />
        </VisitorFormRow>
      </div>

      {/* The contract made visible: what is not listed here is not written. */}
      <div className="visitor-card">
        <p className="visitor-card__title">{t("react_visitors_bulk_changes_title")}</p>
        {changes.length === 0 ? (
          <p className="visitor-card__hint">{t("react_visitors_bulk_no_changes")}</p>
        ) : (
          <ul className="visitor-bulk-edit__changes">
            {changes.map(({ key, value }) => (
              <li className="visitor-bulk-edit__change" key={key}>
                <span className="visitor-detail-row__label">{labelFor(key)}</span>
                <span className="visitor-detail-row__value">
                  {value || t("react_visitors_bulk_cleared")}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* §7, 24-October-2026. One PATCH per visit behind the scenes, so each visitor gets their
          own `visit_updated` email — and only those whose visible details actually changed.
          Hidden when there is nothing to save, because the toggle would have nothing to govern. */}
      {changes.length > 0 && (
        <div className="visitor-card">
          <label className="visitor-toggle">
            <input
              type="checkbox"
              checked={sendEmail}
              onChange={() => setSendEmail((current) => !current)}
              className="visitor-toggle__input"
            />
            <span className="visitor-toggle__text">
              <span className="visitor-toggle__label">
                {t("react_visitors_send_update_email_all")}
              </span>
              <span className="visitor-toggle__hint">
                {t("react_visitors_send_update_email_all_hint")}
              </span>
            </span>
          </label>
        </div>
      )}

      <div className="visitor-wizard__actions">
        <button
          type="button"
          className="visitor-btn visitor-btn--secondary"
          onClick={onBack}
          disabled={isSubmitting}
        >
          {t("react_visitors_back")}
        </button>

        <button
          type="button"
          className="visitor-btn visitor-btn--secondary"
          onClick={submit}
          disabled={isSubmitting || changes.length === 0}
        >
          {t("react_visitors_save_changes")}
        </button>
      </div>
    </div>
  );
};

export default VisitorBulkEditScreen;
